import React, { FC, ReactNode } from 'react';
import { makeStyles } from '@material-ui/styles';
import { Box, Container, Grid, Theme, Typography, useMediaQuery } from '@material-ui/core';
import { PageLink } from "components/common/atoms";

export interface InlineImageTextBlocksItem {
  title: string;
  description: { text: string; href?: string };
  image: ReactNode;
}

interface InlineImageTextBlocksProps {
  data: Array<InlineImageTextBlocksItem>;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(32, 0, 20),
    backgroundColor: theme.palette.secondary.main,
  },
  title: {
    color: theme.palette.common.white,
    marginBottom: theme.spacing(2),
  },
  text: {
    color: theme.palette.customColors.contrastText,
    fontWeight: 600,
    [theme.breakpoints.down('xs')]: {
      fontSize: 14,
    },
  },
  imageBlockWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  image: {
    marginRight: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      marginRight: theme.spacing(5),
    },
  },
  gridElement: {
    '&:not(:last-child)': {
      marginBottom: theme.spacing(8),
      [theme.breakpoints.up('md')]: {
        marginBottom: 0,
      },
    },
  },
}));

const InlineImageTextBlocks: FC<InlineImageTextBlocksProps> = ({ data }) => {
  const classes = useStyles();
  const isMediumUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  return (
    <Box className={classes.root}>
      <Container maxWidth={false} fixed>
        <Grid container spacing={isMediumUp ? 6 : 0}>
          {data.map((card, index, array) => (
            <Grid
              item
              lg={4}
              md={index === array.length - 1 ? 12 : 6}
              xs={12}
              key={index}
              className={classes.gridElement}
            >
              <Box className={classes.imageBlockWrapper}>
                <Box className={classes.image}>{card.image}</Box>
                <Box>
                  <Typography variant="h4" className={classes.title}>
                    {card.title}
                  </Typography>
                  {card.description.href ? (
                    <PageLink
                      target="_blank"
                      href={card.description.href}
                      className={classes.text}
                      rel="noreferrer"
                    >
                      {card.description.text}
                    </PageLink>
                  ) : (
                    <Typography className={classes.text}>{card.description.text}</Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default InlineImageTextBlocks;
